import { useCallback, useEffect, useRef, useState } from "react";
import { API_URL } from "constants/index";
import { RentalContract } from "shared/types/rental-contracts";
import { toast } from "react-toastify";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { useSession } from "@clerk/clerk-react";
import type { IndexationActiveTab } from "./indexationCache";

export const PAGE_SIZE = 50;

export interface IndexationFilters {
  searchText?: string;
  buildingIds?: string[];
  timeRangeFilter?: string[];
  indexationStatusFilter?: string[];
}

export interface IndexationCounts {
  never: number;
  pending: number;
  indexed: number;
}

export interface DuplicateUnit {
  unitId: string;
  contractCount: number;
  contractIds: number[];
  buildingId: string | null;
}

export type KpiFilter = "thisMonth" | "nextMonth";

export interface IndexationKPIs {
  unitsCount: number;
  contractsActiveTotal: number;
  contractsLopend: number;
  contractsInOpzeg: number;
  indexedThisYear: number;
  notIndexedThisYear: number;
  toIndexThisMonth: number;
  toIndexNextMonth: number;
  unitsWithMultipleActiveContracts: number;
}

export const EMPTY_KPIS: IndexationKPIs = {
  unitsCount: 0,
  contractsActiveTotal: 0,
  contractsLopend: 0,
  contractsInOpzeg: 0,
  indexedThisYear: 0,
  notIndexedThisYear: 0,
  toIndexThisMonth: 0,
  toIndexNextMonth: 0,
  unitsWithMultipleActiveContracts: 0,
};

export interface UseIndexationDataParams extends IndexationFilters {
  page: number;
  indexationGroup: IndexationActiveTab;
  kpiFilter?: KpiFilter | null;
}

export interface UseIndexationDataResult {
  contracts: RentalContract[];
  total: number;
  counts: IndexationCounts;
  kpis: IndexationKPIs;
  duplicateUnits: DuplicateUnit[];
  isLoading: boolean;
  setContracts: React.Dispatch<React.SetStateAction<RentalContract[]>>;
  refresh: () => void;
}

/**
 * Page-based hook for fetching indexation contract data.
 * Accepts page + indexationGroup from the parent; the parent
 * resets page to 1 when filters or tab change.
 */
export const useIndexationData = (
  params: UseIndexationDataParams,
): UseIndexationDataResult => {
  const { session } = useSession();
  const sessionRef = useRef(session);
  sessionRef.current = session;

  const [contracts, setContracts] = useState<RentalContract[]>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<IndexationCounts>({ never: 0, pending: 0, indexed: 0 });
  const [kpis, setKpis] = useState<IndexationKPIs>(EMPTY_KPIS);
  const [duplicateUnits, setDuplicateUnits] = useState<DuplicateUnit[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Stable refresh counter to force a re-fetch
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  const fetchContracts = useCallback(async () => {
    if (!sessionRef.current) return;

    setIsLoading(true);
    try {
      const urlParams = new URLSearchParams({
        page: String(params.page),
        pageSize: String(PAGE_SIZE),
        indexationGroup: params.indexationGroup,
      });

      if (params.searchText) {
        urlParams.set("searchText", params.searchText);
      }
      if (params.buildingIds && params.buildingIds.length > 0) {
        urlParams.set("buildingIds", params.buildingIds.join(","));
      }
      if (params.timeRangeFilter && params.timeRangeFilter.length > 0) {
        urlParams.set("timeRange", params.timeRangeFilter.join(","));
      }
      if (
        params.indexationStatusFilter &&
        params.indexationStatusFilter.length > 0
      ) {
        urlParams.set(
          "indexationStatus",
          params.indexationStatusFilter.join(","),
        );
      }
      if (params.kpiFilter) {
        urlParams.set("kpiFilter", params.kpiFilter);
      }

      const response = await fetchWithAuth(
        `${API_URL}/rental/contracts/indexation-overview?${urlParams}`,
        { method: "GET" },
        sessionRef.current,
      );

      if (!response.ok) {
        throw new Error(
          `Ophalen van indexatiecontracten mislukt: ${response.status}`,
        );
      }

      const result = await response.json();
      setContracts(result.data ?? []);
      setTotal(result.pagination?.total ?? 0);
      if (result.counts) {
        setCounts(result.counts);
      }
      if (result.kpis) {
        setKpis(result.kpis);
      }
      if (result.duplicateUnits) {
        setDuplicateUnits(result.duplicateUnits);
      }
    } catch (error) {
      console.error("[useIndexationData] Fout bij ophalen contracten:", error);
      toast.error("Fout bij ophalen van indexatiecontracten");
    } finally {
      setIsLoading(false);
    }
  }, [
    params.page,
    params.indexationGroup,
    params.searchText,
    params.buildingIds?.join(","),
    params.timeRangeFilter?.join(","),
    params.indexationStatusFilter?.join(","),
    params.kpiFilter,
    refreshKey,
  ]);

  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  return {
    contracts,
    total,
    counts,
    kpis,
    duplicateUnits,
    isLoading,
    setContracts,
    refresh,
  };
};
